import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/reducers';
import * as AzureFileUploadStore from '../../../redux/middleware/AzureFileUpload.Middleware';
import * as StorageBrowserStore from '../../../redux/middleware/StorageBrowser.Middleware';
import {
  AllowedAzureFileType,
  AzureFileLinkKind,
  AzureFileListLoaderKind,
  AzureFileTarget,
  AzureFileUploadRequest,
  AzureFileUploadResult,
  DrawerStorageFile,
  RegisteredAzureFile,
  TargetStorageFile,
  TargetStorageFolder,
  UploadSasToken,
} from '../../../models/Entities/AzureFiles/AzureFileUpload';
import { AzureFileEntityTypeEnum, StorageOwnerTypeEnum } from '../../../models/Enums/AzureFileTypeEnum';
import { AzureFileTypeEnum } from '../../../models/Enums/AzureFileTypeEnum';
import { ErrorType } from '../../../models/HttpError';
import { getAzureTargetKey, suggestFileType, validateFile } from '../../../functions/AzureFileUpload.Functions';
import {
  StorageEntityRef,
  StorageOwnerNames,
  Translate,
  formatStorageEntityLabel,
} from '../../../functions/StorageBrowser.Functions';
import { selectStorageOwnerNames } from '../../Files/FilesExplorer/FilesExplorer.Functions';

//#region Props

interface IProps {
  open: boolean;
  onDismiss: () => void;
  /** Already translated title */
  title: string;
  target: AzureFileTarget;
  allowedFileTypes: AllowedAzureFileType[];
  /** Files dropped over the list before opening the drawer */
  initialFiles?: File[];
  /** Which owner service lists the registered files */
  fileListLoader: AzureFileListLoaderKind;
  /** Allow several files at once (default true) */
  allowMultiple?: boolean;
}

interface ConnectedProps {
  registeredFiles: RegisteredAzureFile[];
  isLoadingRegisteredFiles: boolean;
  registeredFilesError: ErrorType | undefined;
  /** The registered files answered at least once (statuses can be computed) */
  registeredFilesLoaded: boolean;
  /** Files of the target read from the storage (source of truth) */
  storageFiles: TargetStorageFile[];
  isLoadingStorageFiles: boolean;
  storageFilesLoaded: boolean;
  storageFilesError: ErrorType | undefined;
  /** Protocol / settlement / provider / sponsor names ("Protocolo 31 - PRESTO") */
  ownerNames: StorageOwnerNames;
}

interface DispatchProps {
  UploadAzureFile: (
    request: AzureFileUploadRequest,
    onProgress?: (percent: number) => void
  ) => Promise<AzureFileUploadResult>;
  GetRegisteredAzureFiles: (kind: AzureFileListLoaderKind, target: AzureFileTarget) => Promise<void>;
  GetTargetStorageFiles: (target: AzureFileTarget, folders: TargetStorageFolder[]) => Promise<void>;
  RequestPreviewSas: (path: string, download: boolean) => Promise<UploadSasToken>;
  ResolveEntityNames: (refs: StorageEntityRef[]) => Promise<void>;
  /** Retries only the register call of an uploaded blob (contract §9.5) */
  RegisterAzureFile: (kind: AzureFileListLoaderKind, target: AzureFileTarget, path: string) => Promise<unknown>;
  /** Links an existing storage file (contract §9.3) */
  LinkAzureFile: (kind: AzureFileLinkKind, target: AzureFileTarget, path: string) => Promise<unknown>;
  /** Removes a link (never deletes the blob) */
  UnlinkAzureFile: (kind: AzureFileLinkKind, target: AzureFileTarget, fileId: string) => Promise<void>;
}

export type Props = IProps & ConnectedProps & DispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps): ConnectedProps => {
  const targetKey = getAzureTargetKey(ownProps.target);
  const entry = state.AzureFiles?.byTarget[targetKey];
  const storage = state.AzureFiles?.storageByTarget?.[targetKey];
  return {
    registeredFiles: entry?.files ?? [],
    isLoadingRegisteredFiles: entry?.isLoading ?? false,
    registeredFilesError: entry?.error,
    registeredFilesLoaded: !!entry?.loadedAt,
    storageFiles: storage?.files ?? [],
    isLoadingStorageFiles: storage?.isLoading ?? false,
    storageFilesLoaded: !!storage?.loadedAt,
    storageFilesError: storage?.error,
    ownerNames: selectStorageOwnerNames(state),
  };
};

export const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): DispatchProps => ({
  UploadAzureFile: (request, onProgress) =>
    dispatch(AzureFileUploadStore.ActionCreators.UploadAzureFile(request, onProgress)),
  GetRegisteredAzureFiles: (kind, target) =>
    dispatch(AzureFileUploadStore.ActionCreators.GetRegisteredAzureFiles(kind, target)),
  GetTargetStorageFiles: (target, folders) =>
    dispatch(AzureFileUploadStore.ActionCreators.GetTargetStorageFiles(target, folders)),
  RequestPreviewSas: (path, download) => dispatch(StorageBrowserStore.ActionCreators.RequestPreviewSas(path, download)),
  ResolveEntityNames: (refs) => dispatch(StorageBrowserStore.ActionCreators.ResolveEntityNames(refs)),
  RegisterAzureFile: (kind, target, path) =>
    dispatch(AzureFileUploadStore.ActionCreators.RegisterAzureFile(kind, target, path)),
  LinkAzureFile: (kind, target, path) => dispatch(AzureFileUploadStore.ActionCreators.LinkAzureFile(kind, target, path)),
  UnlinkAzureFile: (kind, target, fileId) =>
    dispatch(AzureFileUploadStore.ActionCreators.UnlinkAzureFile(kind, target, fileId)),
});

//#endregion

//#region Local upload items

/** unregistered: the blob was uploaded but the owner service could not register it (contract §9.5) */
export type UploadItemStatus = 'pending' | 'uploading' | 'uploaded' | 'unregistered' | 'error';

export interface UploadItem {
  id: string;
  file: File;
  fileType: AzureFileTypeEnum | undefined;
  status: UploadItemStatus;
  progress: number;
  /** Error returned by the upload (already a message, not an i18n key) */
  uploadError?: string;
  /** Blob path relative to the tenant once uploaded (used to retry the register) */
  relativePath?: string;
  /** Why the register failed (already a message) */
  registerError?: string;
  /** A register retry is running */
  registering?: boolean;
}

let sequence = 0;

/** Builds the local items for the selected/dropped files, preselecting the type by extension. */
export const createUploadItems = (
  files: File[],
  allowedTypes: AllowedAzureFileType[]
): UploadItem[] =>
  files.map((file) => ({
    id: `${Date.now()}-${sequence++}-${file.name}`,
    file,
    fileType: allowedTypes.length === 1 ? allowedTypes[0].fileType : suggestFileType(file.name, allowedTypes),
    status: 'pending',
    progress: 0,
  }));

/** Validation i18n key for the item, undefined when valid. */
export const getItemValidation = (item: UploadItem, allowedTypes: AllowedAzureFileType[]): string | undefined =>
  validateFile(item.file, item.fileType, allowedTypes);

/** Items that the "upload all" button will send. */
export const getUploadableItems = (items: UploadItem[], allowedTypes: AllowedAzureFileType[]): UploadItem[] =>
  items.filter(
    (item) => (item.status === 'pending' || item.status === 'error') && !getItemValidation(item, allowedTypes)
  );

/** Merges new files into the current list respecting single/multiple mode. */
export const mergeUploadItems = (
  current: UploadItem[],
  incoming: UploadItem[],
  allowMultiple: boolean
): UploadItem[] => {
  if (!allowMultiple) return incoming.slice(0, 1);
  return [...current, ...incoming];
};

//#endregion

/** Upload error text: the API validation details when present, otherwise the general message. */
export const getUploadErrorMessage = (error: unknown): string => {
  const typed = error as Partial<ErrorType> | undefined;
  const details = (typed?.Errors ?? []).map((detail) => String(detail).trim()).filter(Boolean);
  if (details.length > 0) return Array.from(new Set(details)).join(' ');
  return typed?.ErrorMessage ?? '';
};

//#region Target label ("Sponsor 12 - Sanofi · Protocolo 31 - PRESTO")

// Service receipts are not listed: the service is identified by a GUID and its description is already in the title.
const ENTITY_KIND: Partial<Record<AzureFileEntityTypeEnum, StorageEntityRef['kind']>> = {
  [AzureFileEntityTypeEnum.Provider]: 'provider',
  [AzureFileEntityTypeEnum.Protocol]: 'protocol',
  [AzureFileEntityTypeEnum.Settlement]: 'settlement',
};

/** Entities shown in the drawer header: owner (sponsor / provider), protocol and the target entity. */
export const getTargetEntityRefs = (target: AzureFileTarget): StorageEntityRef[] => {
  const refs: StorageEntityRef[] = [];
  const add = (ref: StorageEntityRef) => {
    if (!refs.some((item) => item.kind === ref.kind && item.id === ref.id)) refs.push(ref);
  };
  const owner = target.owner;
  if (owner?.ownerType === StorageOwnerTypeEnum.Sponsor) add({ kind: 'sponsor', id: owner.ownerId });
  if (owner?.ownerType === StorageOwnerTypeEnum.Provider) add({ kind: 'provider', id: owner.ownerId });
  if (owner?.ownerType === StorageOwnerTypeEnum.Practitioner) add({ kind: 'practitioner', id: owner.ownerId });
  if (owner?.protocolId) add({ kind: 'protocol', id: String(owner.protocolId) });
  const kind = ENTITY_KIND[target.entityType];
  if (kind && target.entityId > 0) add({ kind, id: String(target.entityId) });
  return refs;
};

/** "Liquidación 616 - Liquidación PRESTO" (falls back to "Liquidación 616" while the name loads). */
export const buildTargetLabel = (target: AzureFileTarget, t: Translate, ownerNames: StorageOwnerNames): string =>
  getTargetEntityRefs(target)
    .map((ref) => formatStorageEntityLabel(ref, t, ownerNames))
    .join(' · ');

//#endregion

//#region Manual registration of pending storage files (spec 005, US5)

/** Rows of the storage list that the owner service has not registered yet. */
export const getPendingRows = (rows: DrawerStorageFile[]): DrawerStorageFile[] =>
  rows.filter((row) => row.status === 'pending');

/** registered: the owner service registered it; alreadyRegistered: 409 (someone did it first); error: anything else. */
export type RowRegisterStatus = 'registered' | 'alreadyRegistered' | 'error';

export interface RowRegisterResult {
  path: string;
  name: string;
  status: RowRegisterStatus;
  /** API detail (Errors[] or message) when status = error */
  message?: string;
}

/** Outcome of one register call: 409 counts as already registered (never as a failure). */
export const toRowRegisterResult = (row: Pick<DrawerStorageFile, 'path' | 'name'>, error?: unknown): RowRegisterResult => {
  if (error === undefined) return { path: row.path, name: row.name, status: 'registered' };
  if ((error as Partial<ErrorType>)?.ErrorCode === 409) return { path: row.path, name: row.name, status: 'alreadyRegistered' };
  return { path: row.path, name: row.name, status: 'error', message: getUploadErrorMessage(error) };
};

/** Registers the rows one by one (sequential, readable progress); never rejects. */
export const registerRowsSequentially = async (
  rows: DrawerStorageFile[],
  register: (row: DrawerStorageFile) => Promise<unknown>,
  onResult?: (result: RowRegisterResult) => void
): Promise<RowRegisterResult[]> => {
  const results: RowRegisterResult[] = [];
  for (const row of rows) {
    let result: RowRegisterResult;
    try {
      await register(row);
      result = toRowRegisterResult(row);
    } catch (error: unknown) {
      result = toRowRegisterResult(row, error ?? {});
    }
    results.push(result);
    onResult?.(result);
  }
  return results;
};

//#endregion
